import React, { useEffect, useRef, useState } from "react";
import { showToaster, toasterTypes } from "..";
import { MESSAGES } from "../../utils/Constants";
import { FullscreenOutlined, FullscreenExitOutlined } from "@ant-design/icons";
import ModalComponent from "../../components/common/Modal/modal";

export default function ImageCaptureUpload(props: any) {
  const [image, setImage] = useState<File | null>(null);     // single image
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /* ───────── init camera once ───────── */
  useEffect(() => {
    if (props.isPreview) return;

    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: true });
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        showToaster(toasterTypes.ERROR, "Couldn’t access camera");
      }
    })();
    return () => stream?.getTracks().forEach(t => t.stop());
  }, [props.isPreview]);

  /* ───────── helpers ───────── */
  const capture = () => {
    if (!canvasRef.current || !videoRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx?.drawImage(videoRef.current, 0, 0, 320, 240);
    canvasRef.current.toBlob(blob => {
      if (!blob) return;
      validateAndSave(new File([blob], `capture_${Date.now()}.png`, { type: "image/png" }));
    }, "image/png");
  };

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) validateAndSave(f);
  };

  const validateAndSave = (file: File) => {
    /* ---------- type guard ---------- */
    const allowed = props.acceptFiles?.split("|") ?? ["png", "jpg", "jpeg"];
    const ext = file.type.split("/")[1];
    if (!allowed.includes(ext)) {
      let list = allowed.join(", ").replace(/,([^,]*)$/, " and$1");             // a, b and c
      showToaster(
        toasterTypes.ERROR,
        props.messages?.acceptFiles || MESSAGES.ALLOWED_TYPES.replace("#{value}", list)
      );
      return;
    }

    /* ---------- size guard ---------- */
    if (props.maxSize && file.size > props.maxSize) {
      showToaster(
        toasterTypes.ERROR,
        props.messages?.maxSize || MESSAGES.MAX_SIZE.replace("#value", props.maxSize)
      );
      return;
    }

    /* ---------- total‑size guard (single image) ---------- */
    if (props.maxLimit && file.size > props.maxLimit) {
      const mb = (props.maxLimit / (1024 * 1024)).toFixed(2);
      showToaster(
        toasterTypes.ERROR,
        props.messages?.maxLimit || `Combined file size must not exceed ${mb} MB`
      );
      return;
    }
    setImage(file);
    props.onUpload(file);                               // bubble up just like FileUpload
    showToaster(toasterTypes.SUCCESS, "Image selected!");
  };

  const removeImage = (e: React.MouseEvent) => {
    setImage(null);
    e.stopPropagation();
  };

  const toggleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMaximized(!isMaximized);
  };

  if (props?.value && props?.isPreview) {
    return (
      <div className="image-preview" style={{ position: 'relative', display: 'inline-block' }}>
        <img
          src={`https://go.coguide.in/c1/download/${props?.value}`}
          alt="Image preview"
          style={{ maxWidth: '100%', maxHeight: '200px', cursor: 'pointer' }}
          onClick={() => setIsMaximized(true)}
        />
        <div
          className="maximize-icon"
          onClick={toggleMaximize}
          style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            background: 'rgba(0,0,0,0.5)',
            color: 'white',
            padding: '4px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FullscreenOutlined />
        </div>

        {isMaximized && (
          <ModalComponent
            isOpen={true}
            onClose={() => setIsMaximized(false)}
            header="Image Preview"
            centered={true}
            width="80%"
            body={
              <div style={{ textAlign: 'center', position: 'relative' }}>
                <img
                  src={`https://go.coguide.in/c1/download/${props?.value}`}
                  alt="Full size preview"
                  style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                />
              </div>
            }
            footer={<></>}
          />
        )}
      </div>
    );
  }

  if (!props?.value && props?.isPreview) {
    return (
      <div className="image-preview">
        <p>No Data</p>
      </div>
    )
  }

  /* ───────── render ───────── */
  return (
    <div className="custom-image-upload">
      <section className="upload-container">
        {image ? (
          <div className="preview-with-delete">
            <img src={URL.createObjectURL(image)} alt="preview" />
            <img
              src={`${process.env.PUBLIC_URL}/images/close_circle.svg`}
              onClick={removeImage}
              alt="remove"
            />
          </div>
        ) : (
          <p>
            <span className="upload-placeholder">{props.placeholder}</span>
            <u className="browse-text" onClick={() => fileInput.current?.click()}>
              Browse Image
            </u>
          </p>
        )}

        {/* hidden file input for manual pick */}
        <input
          type="file"
          accept="image/*"
          ref={fileInput}
          style={{ display: "none" }}
          onChange={pickFile}
          disabled={props.isDisabled}
        />

        {/* camera view */}
        <div className="camera-wrapper">
          <video ref={videoRef} autoPlay playsInline />
          <canvas ref={canvasRef} width={320} height={240} hidden />
          <button disabled={props.isDisabled} onClick={capture}>
            Capture
          </button>
        </div>
      </section>
    </div>
  );
}

